import React, { useId, useState } from 'react';
import { Description, Field } from '@headlessui/react';

import {
    valueIsTruthyOrZero,
    replaceNonDigitsWithBlank,
    valueEditedToNotExceedHundredOrGoBelowZero,
} from 'flow/utils';
import {
    wallboardTypes,
    getValidDataPeriodValueInSecondsFromHours,
    getSettingDataPeriodDescription,
} from 'flow/utils/wallboard-utils';
import { twMerge } from 'tailwind-merge';

import Divider from 'flow/components/ui-components/Divider';
import Toggle from 'flow/components/ui-components/Toggle';
import RadioButton from 'flow/components/ui-components/RadioButton';
import useAppContext from 'flow/hooks/useAppContext';

type DataPeriodTypes = 'daily' | 'custom';
type Settings =
    | 'serviceLevelWarningThreshold'
    | 'serviceLevelPeriod'
    | 'dataPeriod';

interface Props {
    dataPeriodTypeInit: DataPeriodTypes;
    dataPeriodInit: number;
    serviceLevelPeriodInit: number;
    serviceLevelWarningThresholdInit: number;
    onSettingSet: Function;
    boardType: 'hunt_groups_multiple' | 'team_custom';
    userHasOmniQueue: boolean;
}

const WallboardSettingsCustomize = ({
    dataPeriodTypeInit,
    dataPeriodInit,
    serviceLevelPeriodInit,
    serviceLevelWarningThresholdInit,
    onSettingSet,
    boardType,
    userHasOmniQueue,
}: Props) => {
    const { i18next } = useAppContext();
    // Read by the threshold input through aria-describedby; the warning
    // switch gets the same text through the surrounding HeadlessUI Field.
    const serviceLevelWarningDescriptionId = `${useId()}-service-level-warning`;

    const [serviceLevelWarningToggled, setServiceLevelWarningToggled] =
        useState(valueIsTruthyOrZero(serviceLevelWarningThresholdInit));
    const [dataPeriodType, setDataPeriodType] = useState(dataPeriodTypeInit);
    const [dataPeriod, setDataPeriod] = useState(
        replaceNonDigitsWithBlank(dataPeriodInit),
    );
    const [serviceLevelPeriod, setServiceLevelPeriod] = useState(
        replaceNonDigitsWithBlank(serviceLevelPeriodInit),
    );
    const [serviceLevelWarningThreshold, setServiceLevelWarningThreshold] =
        useState(replaceNonDigitsWithBlank(serviceLevelWarningThresholdInit));

    const onServiceLevelWarningToggled = (toggleValue: boolean) => {
        setServiceLevelWarningToggled(toggleValue);

        const thresholdValue = toggleValue
            ? serviceLevelWarningThreshold || 0
            : null;
        onSettingSet('serviceLevelWarningThreshold', thresholdValue);
    };

    const cleanAndSetNumberSetting = (setting: Settings, value: any) => {
        const valueCleaned = replaceNonDigitsWithBlank(value);
        const internalStateValue = valueIsTruthyOrZero(valueCleaned)
            ? Number(valueCleaned)
            : '';

        if (setting === 'dataPeriod') {
            setDataPeriod(internalStateValue);
        }
        if (setting === 'serviceLevelPeriod') {
            setServiceLevelPeriod(internalStateValue);
        }
        if (setting === 'serviceLevelWarningThreshold') {
            setServiceLevelWarningThreshold(internalStateValue);
        }

        const returnValueAsNumber = valueCleaned ? Number(valueCleaned) : 0;
        onSettingSet(setting, returnValueAsNumber);
    };

    const onDataPeriodSettingTypeChange = (value: DataPeriodTypes) => {
        setDataPeriodType(value);
        onSettingSet('dataPeriodType', value);
    };

    const getDataPeriodOptionsItems = () => {
        return (
            <React.Fragment>
                <div className="ms-3 mb-2.5 text-xs font-bold">
                    {i18next.t('wallboard.settings.dataperiod')}
                </div>
                <Divider className="my-1" />
                <div className="box-border w-full px-3 text-sm focus:outline-hidden">
                    <RadioButton
                        name={'dataperiod'}
                        key={'daily'}
                        checked={dataPeriodType === 'daily'}
                        onChange={() => onDataPeriodSettingTypeChange('daily')}
                    >
                        {i18next.t('wallboard.settings.dataperiod.daily')}
                    </RadioButton>
                    <RadioButton
                        name={'dataperiod'}
                        key={'custom'}
                        checked={dataPeriodType === 'custom'}
                        onChange={() => onDataPeriodSettingTypeChange('custom')}
                    >
                        {i18next.t('wallboard.settings.limitdataperiod')}
                    </RadioButton>
                </div>
                <Divider className="my-1" />
            </React.Fragment>
        );
    };

    const getDataPeriodTextItem = () => {
        const dataPeriodHours = valueIsTruthyOrZero(dataPeriod)
            ? dataPeriod / 3600
            : '';
        const disabled = dataPeriodType !== 'custom';

        return (
            <div className="mt-4 flex h-5 flex-row items-center ps-3 text-sm text-gray-800">
                <input
                    onChange={(event) =>
                        customDataPeriodChanged(event.target.value)
                    }
                    value={dataPeriodHours}
                    disabled={disabled}
                    className="w-[40px] border-b bg-white text-center focus:outline-hidden"
                />
                <div className={disabled ? 'text-gray-400' : ''}>
                    {i18next.t('duration.hours')}
                </div>
            </div>
        );
    };

    const getServiceLevelPeriodTextItem = () => {
        const dataPeriodDescription = getSettingDataPeriodDescription(
            i18next,
            userHasOmniQueue,
        );

        return (
            <div className="mt-9">
                <div className="ms-3 mb-2.5 text-xs font-bold">
                    {i18next.t('wallboard.settings.servicelevelperiod')}
                </div>
                <div className="mt-1.5 flex flex-row items-center ps-3 text-sm text-gray-800">
                    <input
                        onChange={(event) =>
                            serviceLevelPeriodChanged(event.target.value)
                        }
                        value={serviceLevelPeriod}
                        className="w-[40px] border-b bg-white text-center focus:outline-hidden"
                    />
                    <div className="pb-0.5">
                        {i18next.t('duration.seconds')}
                    </div>
                </div>
                <div className="ms-3 mt-2.5 text-xs text-gray-500">
                    {dataPeriodDescription}
                </div>
            </div>
        );
    };

    const getServiceLevelWarningToggleItem = () => {
        return (
            <div className="mt-9">
                <div className="ms-3 mb-2 text-xs font-bold">
                    {i18next.t('wallboard.settings.servicelevelwarning')}
                </div>
                <Divider className="my-1" />
                <div className="flex justify-between px-3 py-1.5 text-sm">
                    {i18next.t('wallboard.settings.servicelevel.title')}
                    <Toggle
                        value={serviceLevelWarningToggled}
                        onChange={onServiceLevelWarningToggled}
                        text={i18next.t(
                            'wallboard.settings.servicelevel.title',
                        )}
                    />
                </div>
                <Divider className="my-1" />
            </div>
        );
    };

    const customDataPeriodChanged = (value?: number | string) => {
        const valueFormatted = valueIsTruthyOrZero(value)
            ? getValidDataPeriodValueInSecondsFromHours(value)
            : null;
        cleanAndSetNumberSetting('dataPeriod', valueFormatted);
    };

    const serviceLevelPeriodChanged = (value?: number | string) => {
        cleanAndSetNumberSetting('serviceLevelPeriod', value);
    };

    const serviceLevelWarningThresholdChanged = (value?: number | string) => {
        const valueFormatted =
            valueEditedToNotExceedHundredOrGoBelowZero(value);
        cleanAndSetNumberSetting(
            'serviceLevelWarningThreshold',
            valueFormatted,
        );
    };

    const getServiceLevelWarningTextItem = () => {
        return (
            <div className="mt-4">
                <div className="flex flex-row items-center ps-3 text-sm">
                    <input
                        onChange={(event) =>
                            serviceLevelWarningThresholdChanged(
                                event.target.value,
                            )
                        }
                        value={serviceLevelWarningThreshold}
                        disabled={!serviceLevelWarningToggled}
                        aria-describedby={serviceLevelWarningDescriptionId}
                        className={twMerge(
                            'w-[40px] border-b bg-white text-center focus:outline-hidden',
                            serviceLevelWarningToggled
                                ? 'text-gray-800'
                                : 'text-gray-400',
                        )}
                    />
                    <div
                        className={twMerge(
                            'pb-0.5',
                            serviceLevelWarningToggled
                                ? 'text-gray-800'
                                : 'text-gray-400',
                        )}
                    >
                        {'%'}
                    </div>
                </div>
                <Description
                    as="div"
                    id={serviceLevelWarningDescriptionId}
                    className={twMerge(
                        'ms-3 mt-2.5 text-xs',
                        serviceLevelWarningToggled
                            ? 'text-gray-500'
                            : 'text-gray-400',
                    )}
                >
                    {i18next.t('wallboard.settings.servicelevel.description')}
                </Description>
            </div>
        );
    };

    return (
        <div className="flex flex-col">
            {getDataPeriodOptionsItems()}
            {dataPeriodType === 'custom' && getDataPeriodTextItem()}
            {boardType !== wallboardTypes.TEAM_CUSTOM ? (
                <React.Fragment>
                    {getServiceLevelPeriodTextItem()}
                    <Field as="div">
                        {getServiceLevelWarningToggleItem()}
                        {getServiceLevelWarningTextItem()}
                    </Field>
                </React.Fragment>
            ) : null}
        </div>
    );
};
export default React.memo(WallboardSettingsCustomize);
